import type {
  ChatMessage,
  DurableEvent,
  MeInRoom,
  Participant,
  PlayInfo,
  QueueEntry,
  ReactionTally,
  RoomDetail,
  RoomPlaybackSnapshot,
  RoomSnapshot,
} from '@crowdset/contracts';

export const CHAT_CAP = 200;

export interface RoomData {
  room: RoomDetail;
  roomVersion: number;
  playback: RoomPlaybackSnapshot;
  play: PlayInfo | null;
  queue: QueueEntry[];
  participants: Participant[];
  reactions: ReactionTally;
  chat: ChatMessage[];
  me: MeInRoom;
  /** Newest-first system lines (skips, moderation) shown inline in chat. */
  system: SystemLine[];
  /** Per-user counters bumped when someone switches to Woot, to replay the burst animation. */
  bursts: Record<string, number>;
}

export interface SystemLine {
  id: string;
  atMs: number;
  text: string;
  tone: 'info' | 'warn';
}

export function fromSnapshot(snap: RoomSnapshot, prev?: RoomData | null): RoomData {
  // Keep older chat we already have (history paging) and merge the snapshot's latest page.
  const chat =
    prev && prev.room.id === snap.room.id
      ? mergeChat(prev.chat, snap.chat)
      : snap.chat.slice(-CHAT_CAP);
  return {
    room: snap.room,
    roomVersion: snap.roomVersion,
    playback: snap.playback,
    play: snap.play,
    queue: snap.queue,
    participants: snap.participants,
    reactions: snap.reactions,
    chat,
    me: snap.me,
    system: prev && prev.room.id === snap.room.id ? prev.system : [],
    bursts: prev && prev.room.id === snap.room.id ? prev.bursts : {},
  };
}

export function mergeChat(a: ChatMessage[], b: ChatMessage[]): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  for (const m of a) byId.set(m.id, m);
  for (const m of b) byId.set(m.id, m);
  return [...byId.values()].sort((x, y) => compareIds(x.id, y.id)).slice(-CHAT_CAP);
}

function compareIds(a: string, b: string): number {
  // Server ids are bigserial strings; compare numerically when possible.
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return a < b ? -1 : a > b ? 1 : 0;
}

function sys(state: RoomData, text: string, tone: SystemLine['tone'], atMs: number): SystemLine[] {
  return [
    { id: `${atMs}-${state.system.length}-${text.length}`, atMs, text, tone },
    ...state.system,
  ].slice(0, 30);
}

const END_TEXT: Record<string, string> = {
  moderator_skip: 'was skipped by a moderator',
  vote_skip: 'was skipped by the room vote',
  video_unavailable: 'was unavailable and ended early',
  dj_removed: 'ended because the DJ was removed',
  room_archived: 'ended because the room was archived',
};

/** Pure event application. Sequencing (version checks) happens in EventSequencer. */
export function applyEvent(state: RoomData, ev: DurableEvent): RoomData {
  const next: RoomData = { ...state, roomVersion: ev.roomVersion };
  const myId = state.me.userId;
  switch (ev.type) {
    case 'playback.changed': {
      const p = ev.payload;
      next.playback = p.playback;
      next.play = p.play;
      next.reactions = p.reactions;
      next.me = {
        ...state.me,
        isCurrentDj: !!myId && p.play?.dj.id === myId,
        canVote: state.me.userId !== null && state.me.role !== 'guest' && p.play?.dj.id !== myId,
      };
      // Moderator skips are announced by their moderation.action event (with the reason).
      if (
        p.previous &&
        p.previous.endReason !== 'moderator_skip' &&
        END_TEXT[p.previous.endReason] &&
        state.play?.playId === p.previous.playId
      ) {
        next.system = sys(
          state,
          `“${state.play.title}” ${END_TEXT[p.previous.endReason]}.`,
          'warn',
          ev.serverTimeMs,
        );
      }
      if (p.skipped.length > 0) {
        next.system = sys(
          next,
          `Skipped ${p.skipped.length} unplayable track${p.skipped.length > 1 ? 's' : ''} (${p.skipped
            .map((s) => s.reason.replace(/_/g, ' '))
            .join(', ')}).`,
          'info',
          ev.serverTimeMs,
        );
      }
      next.bursts = {};
      return next;
    }
    case 'queue.changed': {
      next.queue = ev.payload.queue;
      next.me = {
        ...state.me,
        inQueue: !!myId && ev.payload.queue.some((q) => q.user.id === myId),
        continuesAfterTurn: state.me.isCurrentDj
          ? ev.payload.currentDjContinues
          : state.me.continuesAfterTurn,
      };
      return next;
    }
    case 'chat.message':
      next.chat = mergeChat(state.chat, [ev.payload.message]);
      return next;
    case 'chat.deleted':
      next.chat = state.chat.map((m) =>
        m.id === ev.payload.messageId ? { ...m, deleted: true, body: '' } : m,
      );
      return next;
    case 'reactions.changed': {
      const bursts = { ...state.bursts };
      if (ev.payload.playId === state.reactions.playId) {
        for (const [uid, v] of Object.entries(ev.payload.votes)) {
          if (v === 'woot' && state.reactions.votes[uid] !== 'woot')
            bursts[uid] = (bursts[uid] ?? 0) + 1;
        }
      }
      next.reactions = ev.payload;
      next.bursts = bursts;
      return next;
    }
    case 'moderation.action': {
      const p = ev.payload;
      const who =
        state.participants.find((x) => x.userId === p.targetUserId)?.displayName ?? 'A user';
      const texts: Record<string, string> = {
        mute: `${who} was muted`,
        kick: `${who} was removed from the room`,
        ban: `${who} was banned`,
        unban: `${who} was unbanned`,
        remove_dj: `${who} was removed from the DJ rotation`,
        // Event order relative to playback.changed is not guaranteed, so don't name the track.
        skip: `${p.actor?.displayName ?? 'A moderator'} skipped the track`,
        delete_chat: 'A message was removed',
        role: `${who}'s role changed`,
      };
      if (p.action !== 'delete_chat') {
        next.system = sys(
          state,
          `${texts[p.action]}${p.reason ? ` — ${p.reason}` : ''}.`,
          'warn',
          ev.serverTimeMs,
        );
      }
      return next;
    }
    case 'room.updated':
      next.room = { ...ev.payload.room, myRole: state.room.myRole };
      return next;
    case 'dj.notice':
      if (ev.payload.userId === myId)
        next.system = sys(state, ev.payload.message, 'warn', ev.serverTimeMs);
      return next;
    default:
      return next;
  }
}
